import { DATE_RANGE_CONJUNCTION } from '@modules/common/components/DateRangePicker/constants';
import { formatDateTimeRange } from '@modules/common/utils';
import {
  useFetchQuantifyReport,
  useGetFormattedDurationFieldLabels,
} from '@modules/quantify/hooks';
import { QuantifyActionType } from '@modules/quantify/store/quantifyActionType';
import { useQuantifyStore } from '@modules/quantify/store/quantifyStore';
import { getNextDuration, getPreviousDuration } from '@modules/quantify/utils';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { createAnalyticsTracker } from 'v2/analytics/events';

const trackFeedbackListSummaryEvents =
  createAnalyticsTracker('timeRangeControls');

const ORIGINAL_RELATIVE_DATE_END_BEFORE = 'Today';

export const useQuantifyTimeRangeControls = () => {
  const fetchReportData = useFetchQuantifyReport();

  const dispatch = useQuantifyStore(({ dispatch }) => dispatch);
  const duration = useQuantifyStore(({ query }) => query.duration);
  const aggregatePeriod = useQuantifyStore(
    ({ query }) => query.aggregatePeriod,
  );
  const traverseCount = useQuantifyStore(
    ({ visual }) => visual.timeRangeTraversal.traverseCount,
  );
  const originalDuration = useQuantifyStore(
    ({ visual }) => visual.timeRangeTraversal.originalDuration,
  );

  const [shouldFetchReportData, setShouldFetchReportData] = useState(false);

  const { durationLabel } = useGetFormattedDurationFieldLabels({
    shouldShowForAllDurationTypes: true,
  });

  useEffect(() => {
    if (shouldFetchReportData) {
      setShouldFetchReportData(false);
      fetchReportData();
    }
  }, [shouldFetchReportData, fetchReportData]);

  const onPrevClick = () => {
    dispatch({
      type: QuantifyActionType.SET_QUERY,
      payload: {
        duration:
          traverseCount - 1 === 0
            ? originalDuration
            : getPreviousDuration(duration, aggregatePeriod),
      },
    });
    dispatch({
      type: QuantifyActionType.SET_VISUAL,
      payload: {
        timeRangeTraversal: {
          originalDuration,
          traverseCount: traverseCount - 1,
        },
      },
    });
    setShouldFetchReportData(true);
    trackFeedbackListSummaryEvents({
      eventName: 'Time Range Controls - Pervious Button Clicked',
      originalDuration: formatDateTimeRange(originalDuration),
      currentDuration: formatDateTimeRange(
        traverseCount - 1 === 0
          ? originalDuration
          : getPreviousDuration(duration, aggregatePeriod),
      ),
    });
  };

  const onNextClick = () => {
    dispatch({
      type: QuantifyActionType.SET_QUERY,
      payload: {
        duration:
          traverseCount + 1 === 0
            ? originalDuration
            : getNextDuration(duration, aggregatePeriod),
      },
    });
    dispatch({
      type: QuantifyActionType.SET_VISUAL,
      payload: {
        timeRangeTraversal: {
          originalDuration,
          traverseCount: traverseCount + 1,
        },
      },
    });
    setShouldFetchReportData(true);
    trackFeedbackListSummaryEvents({
      eventName: 'Time Range Controls - Next Button Clicked',
      originalDuration: formatDateTimeRange(originalDuration),
      currentDuration: formatDateTimeRange(
        traverseCount + 1 === 0
          ? originalDuration
          : getNextDuration(duration, aggregatePeriod),
      ),
    });
  };

  const isNextDisabled = (() => {
    const nextDuration = getNextDuration(duration, aggregatePeriod);
    const now = dayjs().tz();
    const endOfToday = now.clone().endOf('day').tz();

    return (
      nextDuration.startAfter?.isAfter(now) ||
      nextDuration.endBefore?.isAfter(endOfToday)
    );
  })();

  const secondaryXAxisLabel = (() => {
    if (
      (traverseCount && durationLabel.secondary) ||
      originalDuration.type === 'absolute'
    ) {
      return durationLabel.secondary ?? '';
    }

    return `${
      durationLabel.secondary?.split(DATE_RANGE_CONJUNCTION)[0]
    }${DATE_RANGE_CONJUNCTION}${ORIGINAL_RELATIVE_DATE_END_BEFORE}`;
  })();

  return {
    onPrevClick,
    onNextClick,
    isNextDisabled,
    secondaryXAxisLabel,
  };
};
